/**
 * useAssinaturaDetalhes - Hook para detalhes completos de uma assinatura (Recorrência)
 *
 * Consome a RPC get_assinatura_details (jsonb) e mantém o cache fresco via
 * realtime nas tabelas financeiro_assinaturas e financeiro_cobrancas
 * (filtrado por assinatura_id), seguindo o padrão centralizado de
 * subscriptions por página/modal.
 *
 * Zero leitura direta de tabelas — toda informação vem da RPC.
 *
 * @version 1.1.0
 * RELEASE: 2026-06-21 v1.1.0 - Tipagem expõe conta_corrente.checkout_mode e ciclo.checkout_session_slug (RPC v1.6.0) para que o SubscriptionDetailsModal resolva o link via resolveCheckoutUrl (transparente x provider).
 * RELEASE: 2026-06-16 v1.0.1 - Refetch agressivo (staleTime 0, refetchOnWindowFocus, refetchOnMount='always') para refletir alterações feitas em modais aninhados (ChargeDetailsModal).
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 4 do módulo Recorrência)
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { TimelineEventInput } from '@/components/financial/ChargeTimeline';

const logger = createLogger('useAssinaturaDetalhes');

/** Cabeçalho da assinatura mãe (regra) */
export interface AssinaturaDetailsAssinatura {
  id: string;
  tenant_id: string;
  customer_id: string;
  deal_id: string | null;
  status: string;
  frequencia: string;
  tipo_prazo: string;
  ciclos_total: number | null;
  ciclos_gerados: number;
  primeiro_vencimento: string;
  proximo_vencimento: string;
  dia_vencimento: number | null;
  dias_antes_emissao: number;
  // v1.2.0: modo de emissão (dias_antes | dia_fixo)
  tipo_emissao?: 'dias_antes' | 'dia_fixo';
  dia_emissao?: number | null;
  /** v1.3.0: dias para expirar a cobrança após o vencimento (1..60, default 60). */
  dias_expiracao_pos_vencimento?: number;
  valor_total: number;
  moeda: string | null;
  meio_pagamento_id: string;
  conta_corrente_id: string;
  descricao: string | null;
  observacoes: string | null;
  incluir_itens_descricao: boolean | null;
  desconto: Record<string, unknown> | null;
  multa: Record<string, unknown> | null;
  juros: Record<string, unknown> | null;
  reajuste: Record<string, unknown> | null;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  provider_slug: string | null;
  provider_subscription_id: string | null;
  created_at: string;
  updated_at: string;
  canceled_at: string | null;
}

export interface AssinaturaDetailsCustomer {
  name: string | null;
  customer_type: string | null;
  cpf: string | null;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
}

export interface AssinaturaDetailsMeioPagamento {
  id: string;
  nome: string;
  tipo_pagamento: string;
}

export interface AssinaturaDetailsContaCorrente {
  id: string;
  nome: string;
  banco_nome: string | null;
  tipo_conta: string | null;
  provider_slug: string | null;
  /** v1.1.0 — modo de checkout preferido configurado na conta corrente. */
  checkout_mode?: 'transparent' | 'provider' | null;
}

export interface AssinaturaDetailsItem {
  id: string;
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  desconto: number;
  /** Tipo de desconto persistido em metadata (v1.2.1 RPC get_assinatura_details). */
  desconto_tipo?: 'percent' | 'fixed';
  /** Percentual original (0–100) quando desconto_tipo='percent'. */
  desconto_percent?: number;
  total: number | null;
  product_id: string | null;
  product_name: string | null;
}

/** Ciclo (= cobrança filha) com badge de status */
export interface AssinaturaDetailsCiclo {
  cobranca_id: string;
  ciclo_numero: number;
  status: string;
  valor_total: number;
  data_vencimento: string;
  data_pagamento: string | null;
  valor_pago: number | null;
  provider_charge_id: string | null;
  provider_invoice_url: string | null;
  provider_payment_link: string | null;
  /** v1.1.0 — slug do checkout transparente cacheado na cobrança (para resolveCheckoutUrl). */
  checkout_session_slug?: string | null;
  numero_fatura: string | null;
  // v1.1.0 — campos extras retornados pela RPC para alimentar
  // a coluna "N.Fatura" e os atalhos de ação do provedor (PIX/Boleto/Link)
  tipo_cobranca?: string | null;
  provider_slug?: string | null;
  conta_corrente_id?: string | null;
  billing_type?: string | null;
  // v1.1.1 — meio de pagamento por ciclo (exibido na coluna "Meio Pgto.")
  meio_pagamento_id?: string | null;
  meio_pagamento_nome?: string | null;
  meio_pagamento_tipo?: string | null;
  // v1.2.0 — link do comprovante de pagamento (RPC get_assinatura_details v1.9.0)
  // Primário: financeiro_cobrancas.metadata.transaction_receipt_url
  // Fallback: financeiro_parcelas.metadata.transaction_receipt_url da 1ª parcela
  receipt_url?: string | null;
}

export interface AssinaturaDetailsResult {
  assinatura: AssinaturaDetailsAssinatura;
  customer: AssinaturaDetailsCustomer | null;
  meio_pagamento: AssinaturaDetailsMeioPagamento | null;
  conta_corrente: AssinaturaDetailsContaCorrente | null;
  itens: AssinaturaDetailsItem[];
  ciclos: AssinaturaDetailsCiclo[];
  /** v1.2.0 — eventos da timeline (histórico da recorrência) */
  timeline: TimelineEventInput[];
}

/**
 * Busca detalhes completos da assinatura via RPC.
 *
 * @param assinaturaId UUID da assinatura mãe
 * @param open         indica se o modal está aberto (controla enabled/realtime)
 */
export function useAssinaturaDetalhes(
  assinaturaId: string | undefined | null,
  open: boolean,
) {
  const queryClient = useQueryClient();

  // Realtime: invalida cache quando a regra ou os ciclos mudam
  useEffect(() => {
    if (!assinaturaId || !open) return;

    // Sufixo único evita reuso de canal já subscrito (StrictMode/reabertura rápida do modal),
    // que dispararia "cannot add postgres_changes callbacks ... after subscribe()".
    const channelName = `assinatura-rt-${assinaturaId}-${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'financeiro_assinaturas', filter: `id=eq.${assinaturaId}` },
        () => {
          logger.debug('Assinatura alterada via realtime', { assinaturaId });
          queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'financeiro_cobrancas', filter: `assinatura_id=eq.${assinaturaId}` },
        () => {
          logger.debug('Ciclo da assinatura alterado via realtime', { assinaturaId });
          queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'financeiro_assinatura_eventos', filter: `assinatura_id=eq.${assinaturaId}` },
        () => {
          logger.debug('Novo evento na timeline da assinatura', { assinaturaId });
          queryClient.invalidateQueries({ queryKey: ['assinatura-detalhes', assinaturaId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [assinaturaId, open, queryClient]);

  return useQuery({
    queryKey: ['assinatura-detalhes', assinaturaId],
    queryFn: async (): Promise<AssinaturaDetailsResult> => {
      if (!assinaturaId) throw new Error('assinaturaId é obrigatório');

      const { data, error } = await (supabase.rpc as any)('get_assinatura_details', {
        p_assinatura_id: assinaturaId,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('Erro ao buscar detalhes da assinatura', { error: error.message });
        throw error;
      }

      return data as unknown as AssinaturaDetailsResult;
    },
    enabled: !!assinaturaId && open,
    // v1.0.1 — Refetch agressivo: garante que ao reabrir o modal ou voltar
    // o foco para a janela, os ciclos reflitam ações feitas em modais
    // aninhados (Reenviar Provider, Cancelar, Editar etc.).
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
}
